import React, { useMemo, useState } from "react";
import { Form } from "react-final-form";
import { Box, Button } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { enqueueSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectVersionsByProjectId, useGetVersionsQuery } from "fond/api";
import { Autocomplete, TextField } from "fond/form/fields";
import { updateAttachments } from "fond/redux/attachments";
import { AppThunkDispatch, Attachment, BaseAttachment, Version } from "fond/types";
import { useAppDispatch, useAppSelector } from "fond/utils/hooks";
import { compose, required } from "fond/utils/validation";
import { Modal } from "fond/widgets";
import { ModalCloseHandler } from "fond/widgets/Modal";

import { NO_VERSION_LABEL } from "./types";

type SingleEditProps = {
  /**
   * The attachment to edit
   */
  attachment: BaseAttachment | Attachment;
  /**
   * Multiple attachments for bulk editing. When provided, the name field is hidden.
   */
  attachments?: never;
  /**
   * An array of items to validate the new name against. If provided, the `validateUniqueName` prop must also be provided
   * otherwise the validation will not run.
   */
  items?: Attachment[] | null;
  /**
   * If true, the modal will validate that the new name is unique within the given set of items.
   * When provided, the `items` prop must also be provided otherwise the validation will not run.
   */
  validateUniqueName?: boolean;
  /**
   * Callback function to handle the onClose of the modal
   */
  onClose: ModalCloseHandler<Attachment | BaseAttachment | Attachment[]>;
  /**
   * When provided, this handler is used instead of dispatching an update, for renaming a file that has not yet been
   * uploaded (i.e. it has no attachment ID). The version field is not shown in this case.
   */
  onRename?: (formData: { name: string }) => Promise<void>;
  /**
   * If false, the version field is hidden. Defaults to true.
   */
  showVersionField?: boolean;
};

type BulkEditProps = {
  /**
   * Not used in bulk edit mode.
   */
  attachment?: never;
  /**
   * Multiple attachments for bulk editing. When provided, the name field is hidden.
   */
  attachments: Attachment[];
  items?: Attachment[] | null;
  validateUniqueName?: boolean;
  onClose: ModalCloseHandler<Attachment | BaseAttachment | Attachment[]>;
  onRename?: never;
  /**
   * If false, the version field is hidden. Defaults to true.
   */
  showVersionField?: boolean;
};

type RenameAttachmentModalProps = SingleEditProps | BulkEditProps;

const isAttachment = (attachment: BaseAttachment | Attachment): attachment is Attachment => {
  return (attachment as Attachment).ID !== undefined;
};

interface IEditAttachmentFormData {
  name: string;
  versionId: string | null;
}

export const EditAttachmentModal: React.FC<RenameAttachmentModalProps> = (props: RenameAttachmentModalProps) => {
  const { items, validateUniqueName, onClose, showVersionField = true } = props;
  const isBulkEdit = "attachments" in props && props.attachments != null;
  const attachment = isBulkEdit ? props.attachments[0] : props.attachment;
  const onRename = isBulkEdit ? undefined : props.onRename;

  const dispatch: AppThunkDispatch = useAppDispatch();
  const [saving, setSaving] = useState(false);
  const isExistingAttachment = isAttachment(attachment);
  const projectId = isExistingAttachment ? attachment.ProjectID : undefined;

  // Fetch versions for the attachment's project
  useGetVersionsQuery(projectId ?? skipToken);
  const versions = useAppSelector((state) => selectVersionsByProjectId(state, projectId ?? null));

  // Map versions to Autocomplete options format
  const versionOptions = [{ ID: "", Name: NO_VERSION_LABEL }, ...(versions?.map((item: Version) => ({ ID: item.ID, Name: item.Name })) || [])];

  const handleValidateName = (value: string): string | undefined => {
    if (!validateUniqueName || !items) return undefined;

    const isChanged = value !== attachment.Name;
    const existingId = isExistingAttachment ? attachment.ID : undefined;
    const nameExists = items.find((item) => item.Name === value && item.Extension === attachment.Extension && item.ID !== existingId);

    if (isChanged && nameExists) {
      return `There is already an attachment named ${value}.${attachment.Extension}.`;
    }
    return undefined;
  };

  // Determine the initial version value for bulk edit.
  // Returns null when versions are mixed (no pre-selection), "" when all have no version,
  // or the shared version ID when all attachments share the same version.
  const bulkInitialVersionId = useMemo((): string | null => {
    if (!isBulkEdit) return "";
    const allAttachments = props.attachments;
    const firstVersionId = allAttachments[0]?.Version?.ID ?? "";
    const allSame = allAttachments.every((a) => (a.Version?.ID ?? "") === firstVersionId);
    return allSame ? firstVersionId : null;
  }, [isBulkEdit, props]);

  const handleOnSubmit = async (formData: IEditAttachmentFormData) => {
    setSaving(true);

    if (onRename) {
      try {
        await onRename({ name: formData.name });
        setSaving(false);
        onClose(new SubmitEvent("submit"), "complete", { ...attachment, Name: formData.name });
      } catch (error) {
        enqueueSnackbar("Failed to rename file.");
        setSaving(false);
      }
      return;
    }

    if (isBulkEdit) {
      const { versionId } = formData;
      const updatedAttachments: Attachment[] = props.attachments.map((a) => ({
        ...a,
        // null means mixed/unchanged — preserve the original version for each attachment.
        // "" means the user explicitly selected "No version".
        ...(versionId !== null
          ? {
              Version: versionId ? { ID: versionId } : null,
            }
          : {}),
      }));

      try {
        await dispatch(updateAttachments(updatedAttachments));
        enqueueSnackbar(`Updated ${updatedAttachments.length} attachments`);
        setSaving(false);
        onClose(new SubmitEvent("submit"), "complete", updatedAttachments);
      } catch (error) {
        enqueueSnackbar("Failed to update attachments.");
        setSaving(false);
      }
      return;
    }

    const updatedAttachment: Attachment = {
      ...(attachment as Attachment),
      Name: formData.name,
      Version: formData.versionId ? { ID: formData.versionId } : null,
    };

    try {
      await dispatch(updateAttachments([updatedAttachment]));
      enqueueSnackbar(`Updated attachment: ${attachment.Name}.${attachment.Extension}`);
      setSaving(false);
      onClose(new SubmitEvent("submit"), "complete", updatedAttachment);
    } catch (error) {
      enqueueSnackbar("Failed to update attachment.");
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      header={onRename ? "Rename File" : isBulkEdit ? "Edit Attachments" : "Edit Attachment"}
      data-testid="edit-attachment-modal"
      onClose={(event, reason) => onClose(event, reason, isBulkEdit ? props.attachments : attachment)}
      content={
        <Form<IEditAttachmentFormData>
          initialValues={{
            name: isBulkEdit ? "" : attachment.Name,
            versionId: isBulkEdit ? bulkInitialVersionId : attachment.Version?.ID || "",
          }}
          onSubmit={handleOnSubmit}
        >
          {({ handleSubmit }) => (
            <form onSubmit={handleSubmit} id="edit-attachment-form">
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {!isBulkEdit && (
                  <TextField
                    data-testid="attachment-name-input"
                    name="name"
                    autoComplete="off"
                    required
                    label="Name"
                    helperText={`File extension: .${attachment.Extension}`}
                    fullWidth
                    autoFocus
                    validate={compose(required, handleValidateName)}
                  />
                )}

                {isExistingAttachment && showVersionField && (
                  <Autocomplete<{ ID: string; Name: string }>
                    data-testid="attachment-version-select"
                    name="versionId"
                    label={isBulkEdit ? "Version" : "Version (Optional)"}
                    options={versionOptions}
                    getOptionLabel={(option) => option?.Name ?? ""}
                    getOptionValue={(option) => option?.ID ?? ""}
                    helperText={isBulkEdit ? "Set the version for all selected attachments" : "Associate this attachment with a specific version"}
                    fullWidth
                    size="small"
                    required={isBulkEdit}
                  />
                )}
              </Box>
            </form>
          )}
        </Form>
      }
      actions={
        <>
          <Button
            data-testid="edit-attachment-modal-cancel-button"
            color="primary"
            onClick={(e) => onClose(e, "cancel", isBulkEdit ? props.attachments : attachment)}
          >
            Cancel
          </Button>
          <LoadingButton data-testid="edit-attachment-modal-save-button" color="primary" type="submit" form="edit-attachment-form" loading={saving}>
            Save
          </LoadingButton>
        </>
      }
    />
  );
};
